import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { DEFAULT_ADDRESS_ID } from "constants/settings/replyAddresses";
import { useAgentFlag } from "hooks/use-agent-flag";
import { selectCanManageCustomMailboxes } from "store/entities/mailboxes/selectors";
import { ReplyAddressActions } from "store/entities/reply-addresses/actions";
import { selectReplyAddresses } from "store/entities/reply-addresses/selectors";
import { withVar } from "themes/helpers";

import { NotAllowedBanner } from "../NotAllowedBanner";
import { trackEmailSectionOpened } from "../analytics";
import { EmailSettingsSubsection } from "../routes";

import DeleteAddressModal from "./DeleteAddressModal";
import { Headline } from "./Headline";
import { ReplyAddressesTip } from "./ReplyAddressesTip";
import { trackAddModalOpened } from "./analytics";
import PreviewAddressModal from "./preview-modal/PreviewAddressModal";
import { ReplyAddressesList } from "./reply-address-list/ReplyAddressList";
import AddressSettingsModal from "./settings-modal/AddressSettingsModal";

interface IAddReplyAddressProps {
  onAdd: () => void;
}

const AddReplyAddress: FC<IAddReplyAddressProps> = ({ onAdd }) => {
  const canManageInboxes = useSelector(selectCanManageCustomMailboxes);

  return (
    <Button disabled={!canManageInboxes} onClick={onAdd} kind="secondary" data-testid="add-reply-address-button">
      Add reply address
    </Button>
  );
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const ReplyAddressesSection = () => {
  const dispatch = useDispatch();
  const replyAddresses = useSelector(selectReplyAddresses);
  const canManageInboxes = useSelector(selectCanManageCustomMailboxes);

  const [hasClosedTip] = useAgentFlag("hasClosedReplyAddressesTip");

  const [settingsModalState, setSettingsModalState] = useState<{
    id?: string;
    fromName: string | null;
    isEdit?: boolean;
  } | null>(null);

  const [previewModalState, setPreviewModalState] = useState<{
    fromName: string | null;
    email: string;
    verified: boolean;
  } | null>(null);

  const [deleteModalState, setDeleteModalState] = useState<{
    id?: string;
    fromName: string | null;
  } | null>(null);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeSettingsModal = () => setSettingsModalState(null);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closePreviewModal = () => setPreviewModalState(null);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeDeleteModal = () => setDeleteModalState(null);

  useEffect(() => {
    trackEmailSectionOpened(EmailSettingsSubsection.ReplyAddresses);
  }, []);

  const handleAdd = useCallback(() => {
    trackAddModalOpened();
    setSettingsModalState({ fromName: "" });
  }, [setSettingsModalState]);

  const handleDelete = useCallback(() => {
    if (!deleteModalState) return;
    if (!deleteModalState.id) {
      return;
    }
    dispatch(
      ReplyAddressActions.delete({
        id: deleteModalState.id === DEFAULT_ADDRESS_ID ? null : deleteModalState.id,
        displayName: deleteModalState.fromName,
      })
    );
  }, [deleteModalState, dispatch]);

  return (
    <>
      <Headline />
      <Flex column gap={withVar(SpacingToken.Spacing6)}>
        <Flex column gap={withVar(SpacingToken.Spacing8)}>
          {!canManageInboxes && <NotAllowedBanner section={EmailSettingsSubsection.ForwardingAddresses} />}
          <Block>
            <AddReplyAddress onAdd={handleAdd} />
          </Block>
          <ReplyAddressesList
            showPreviewModal={setPreviewModalState}
            showEditModal={setSettingsModalState}
            showDeleteModal={setDeleteModalState}
            replyAddresses={replyAddresses}
          />
        </Flex>
        {!hasClosedTip && <ReplyAddressesTip />}
      </Flex>
      {previewModalState && <PreviewAddressModal {...previewModalState} onClose={closePreviewModal} />}
      {settingsModalState && <AddressSettingsModal {...settingsModalState} onClose={closeSettingsModal} />}
      {deleteModalState && (
        <DeleteAddressModal
          fromName={deleteModalState.fromName}
          replyAddress={deleteModalState.id ? replyAddresses[deleteModalState.id] : null}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
        />
      )}
    </>
  );
};
